import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useContentfulContentTypeEntry } from '@order/Contentful';

/**
 * Returns the contentful data for the bag.
 */
export const useBagContent = () => {
  const contentfulData = useContentfulContentTypeEntry<BagContent>({
    contentType: 'bag',
  });

  const content = contentfulData.data?.fields;
  const { formatMessage } = useIntl();

  return useMemo(
    () => ({
      viewMenuCta: content?.viewMenuCta ?? formatMessage(fallbacks.viewMenuCta), // prettier-ignore
      viewMenuA11y: content?.viewMenuA11y ?? formatMessage(fallbacks.viewMenuA11y), // prettier-ignore
      continueToCheckoutCta: content?.continueToCheckoutCta ?? formatMessage(fallbacks.continueToCheckoutCta), // prettier-ignore
      orderPlaybackCta: content?.orderPlaybackCta ?? formatMessage(fallbacks.orderPlaybackCta), // prettier-ignore
      deliveryDisclosure: content?.deliveryDisclosureV2 ?? formatMessage(fallbacks.deliveryDisclosure, deliveryPlaceholders), // prettier-ignore
      outpostDisclosure: content?.outpostDisclosure ?? formatMessage(fallbacks.outpostDisclosure), // prettier-ignore
      timeslotUnavailableWarning: content?.timeslotUnavailableWarning ?? formatMessage(fallbacks.timeslotUnavailableWarning, timeslotUnavailableWarningPlaceholders), // prettier-ignore
      timeslotUnavailableWarningContinueCta: content?.timeslotUnavailableWarningContinueCta ?? formatMessage(fallbacks.timeslotUnavailableWarningContinueCta), // prettier-ignore
      timeslotUnavailableWarningBackCta: content?.timeslotUnavailableWarningBackCta ?? formatMessage(fallbacks.timeslotUnavailableWarningBackCta), // prettier-ignore
      nextDayOrderWarningRegular: content?.nextDayOrderWarningRegular ?? formatMessage(fallbacks.nextDayOrderWarningRegular), // prettier-ignore
      nextDayOrderWarningOutpost: content?.nextDayOrderWarningOutpost ?? formatMessage(fallbacks.nextDayOrderWarningOutpost), // prettier-ignore
    }),
    [content, formatMessage],
  );
};

// ─── Fallbacks ──────────────────────────────────────────────────────────────

const fallbacks = defineMessages({
  viewMenuCta: {
    defaultMessage: 'Menu',
    description: 'Bag | CTAs | Menu',
  },
  viewMenuA11y: {
    defaultMessage: 'View menu',
    description: 'Bag | CTAs | View menu',
  },
  continueToCheckoutCta: {
    defaultMessage: 'Continue to checkout',
    description: 'Bag | CTAs | Continue to checkout',
  },
  orderPlaybackCta: {
    defaultMessage: 'Looks good',
    description: 'Bag | CTAs | Order playback',
  },
  deliveryDisclosure: {
    defaultMessage:
      'Delivery has a {minimum} minimum and includes higher menu prices and a {fee} delivery fee that goes directly to our delivery partner.',
    description: 'Bag | CTAs | Delivery Disclosure',
  },
  outpostDisclosure: {
    defaultMessage: 'Higher menu prices apply for Outpost.',
    description: 'Bag | CTAs | Outpost Disclosure',
  },
  timeslotUnavailableWarning: {
    defaultMessage:
      'We’ve adjusted your pickup time to {current} (previously {previous}) to give the store enough time to prepare your order.',
    description: 'Bag | CTAs | Timeslot Unavailable Warning',
  },
  timeslotUnavailableWarningContinueCta: {
    defaultMessage: 'Continue placing order',
    description: 'Bag | CTAs | Timeslot Unavailable Warning Continue CTA',
  },
  timeslotUnavailableWarningBackCta: {
    defaultMessage: 'Select a different time',
    description: 'Bag | CTAs | Timeslot Unavailable Warning Back CTA',
  },
  nextDayOrderWarningRegular: {
    defaultMessage:
      'Heads up - Unfortunately, the store is closing and orders can’t be placed for today. Please select a future date or time to continue.',
    description: 'Bag | CTAs | Next Day Order Warning Regular',
  },
  nextDayOrderWarningOutpost: {
    defaultMessage:
      'This outpost is no longer accepting new orders today. Order ahead or pick up/deliver from another location.',
    description: 'Bag | CTAs | Next Day Order Warning Outpost',
  },
});

// ─── Constants ──────────────────────────────────────────────────────────────

const deliveryPlaceholders = { minimum: '{minimum}', fee: '{fee}' };
const timeslotUnavailableWarningPlaceholders = {
  current: '{current}',
  previous: '{previous}',
};

// ─── Types ──────────────────────────────────────────────────────────────────

type BagContent = {
  viewMenuCta: string;
  viewMenuA11y: string;
  continueToCheckoutCta: string;
  orderPlaybackCta: string;
  deliveryDisclosureV2: string;
  outpostDisclosure: string;
  timeslotUnavailableWarning: string;
  timeslotUnavailableWarningContinueCta: string;
  timeslotUnavailableWarningBackCta: string;
  nextDayOrderWarningRegular: string;
  nextDayOrderWarningOutpost: string;
};
